import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import PageHeader from '../../common/PageHeader'
import Section from '../../common/Section'
import Alert from '../../common/Alert'
import CodeBlock, { SimpleCodeBlock, InlineCode } from '../../common/CodeBlock'
import { ParamTable } from '../../common/Table'
import { useLocale } from '../../../i18n/useLocale'

export default function Authentication() {
  const { urls, phone, examples, brand } = useLocale()

  return (
    <Box>
      <PageHeader
        title="Authentication"
        description="Learn how to authenticate your API requests using your API key."
        breadcrumbs={[
          { label: 'Getting Started', path: '/getting-started' },
          { label: 'Authentication' },
        ]}
      />

      <Alert type="warning" title="Keep Your API Key Secure">
        Never expose your API key in client-side code or public repositories.
        Always make API calls from your backend server.
      </Alert>

      <Section title="API Key Authentication" divider={false}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {brand.name} uses API key authentication for protected endpoints. Your API key identifies
          your partner account and must be included in the <InlineCode>X-API-KEY</InlineCode> header.
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          You will receive your API key during partner onboarding, and you can view or rotate it
          yourself at any time in the <strong>Customer Portal → Settings</strong>. The same key is
          used for all protected API endpoints.
        </Typography>
        <Alert type="warning" title="Decode the key before use">
          The key shown in the Customer Portal is <strong>Base64-encoded</strong>. Decode it and put
          the <em>decoded</em> value in the <InlineCode>X-API-KEY</InlineCode> header — sending the
          encoded form results in <InlineCode>401 Unauthorized</InlineCode>.
        </Alert>
      </Section>

      <Section title="Public Endpoints">
        <Typography variant="body1" sx={{ mb: 2 }}>
          Some endpoints are publicly accessible and do not require authentication:
        </Typography>
        <Box component="ul" sx={{ pl: 3, '& li': { mb: 1 } }}>
          <li><InlineCode>GET /parcels/status</InlineCode> - Single parcel tracking by barcode</li>
          <li><InlineCode>GET /apm</InlineCode> - APM locations (used by the APM Finder widget)</li>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          All other endpoints require the <InlineCode>X-API-KEY</InlineCode> header.
          Each endpoint page indicates whether it is public or requires authentication.
        </Typography>
      </Section>

      <Section title="Required Headers">
        <Typography variant="body1" sx={{ mb: 2 }}>
          All API requests must include these headers:
        </Typography>

        <ParamTable
          params={[
            {
              name: 'X-API-KEY',
              type: 'string',
              required: true,
              description: 'Your unique API key provided during partner onboarding.',
            },
            {
              name: 'Content-Type',
              type: 'string',
              required: true,
              description: 'Must be "application/json" for all API requests.',
            },
          ]}
        />
      </Section>

      <Section title="Making Authenticated Requests">
        <Typography variant="body1" sx={{ mb: 2 }}>
          Include your API key in the request headers:
        </Typography>

        <CodeBlock
          title="Authenticated API Request"
          examples={[
            {
              language: 'bash',
              label: 'cURL',
              code: `curl -X POST "${urls.apiBase}/parcel/b2c" \\
  -H "Content-Type: application/json" \\
  -H "X-API-KEY: your-api-key" \\
  -d '[{
    "receiverName": "${examples.personName.full}",
    "receiverEmail": "john@example.com",
    "receiverPhone": "${phone.example}",
    "destination": "ke0001",
    "size": "M"
  }]'`,
            },
            {
              language: 'javascript',
              label: 'Node.js',
              code: `const axios = require('axios');

async function createParcel(parcelData) {
  const API_KEY = process.env.SMARTPOST_API_KEY;

  const response = await axios.post(
    '${urls.apiBase}/parcel/b2c',
    parcelData,
    {
      headers: {
        'Content-Type': 'application/json',
        'X-API-KEY': API_KEY,
      },
    }
  );

  return response.data;
}

// Usage
const parcels = [{
  receiverName: '${examples.personName.full}',
  receiverEmail: 'john@example.com',
  receiverPhone: '${phone.example}',
  destination: 'ke0001',
  size: 'M'
}];

createParcel(parcels).then(console.log);`,
            },
            {
              language: 'python',
              label: 'Python',
              code: `import requests
import os

def create_parcel(parcel_data: list) -> dict:
    API_KEY = os.environ['SMARTPOST_API_KEY']

    response = requests.post(
        '${urls.apiBase}/parcel/b2c',
        json=parcel_data,
        headers={
            'Content-Type': 'application/json',
            'X-API-KEY': API_KEY,
        }
    )

    return response.json()

# Usage
parcels = [{
    'receiverName': '${examples.personName.full}',
    'receiverEmail': 'john@example.com',
    'receiverPhone': '${phone.example}',
    'destination': 'ke0001',
    'size': 'M'
}]

result = create_parcel(parcels)
print(result)`,
            },
            {
              language: 'php',
              label: 'PHP',
              code: `<?php

function createParcel(array $parcelData): array {
    $apiKey = getenv('SMARTPOST_API_KEY');

    $ch = curl_init('${urls.apiBase}/parcel/b2c');
    curl_setopt_array($ch, [
        CURLOPT_RETURNTRANSFER => true,
        CURLOPT_POST => true,
        CURLOPT_POSTFIELDS => json_encode($parcelData),
        CURLOPT_HTTPHEADER => [
            'Content-Type: application/json',
            'X-API-KEY: ' . $apiKey,
        ],
    ]);

    $response = curl_exec($ch);
    curl_close($ch);

    return json_decode($response, true);
}

// Usage
$parcels = [[
    'receiverName' => '${examples.personName.full}',
    'receiverEmail' => 'john@example.com',
    'receiverPhone' => '${phone.example}',
    'destination' => 'ke0001',
    'size' => 'M'
]];

$result = createParcel($parcels);
print_r($result);`,
            },
          ]}
        />
      </Section>

      <Section title="API Key Management">
        <Typography variant="body1" sx={{ mb: 2 }}>
          Your API key is managed in the <strong>Customer Portal → Settings</strong> by a logged-in
          account administrator: view the current key or regenerate it there. There are no API
          endpoints for key management.
        </Typography>
        <Alert type="warning">
          Regenerating your API key immediately invalidates the previous key.
          Update your integration before regenerating in production.
        </Alert>
      </Section>

      <Section title="Error Responses">
        <Typography variant="body1" sx={{ mb: 2 }}>
          Authentication errors return standard HTTP status codes:
        </Typography>

        <ParamTable
          params={[
            {
              name: '401 Unauthorized',
              type: 'Error',
              required: false,
              description: 'Invalid or missing API key.',
            },
            {
              name: '403 Forbidden',
              type: 'Error',
              required: false,
              description: 'Valid API key but insufficient permissions for the requested resource.',
            },
          ]}
        />

        <Typography variant="body1" sx={{ mt: 2 }}>
          Example error response:
        </Typography>

        <SimpleCodeBlock
          code={`{
  "timestamp": "2026-01-15 14:30:00",
  "status": 401,
  "errorCode": "SERVICE_UNAUTHORIZED",
  "message": "Invalid API key",
  "fieldErrors": []
}`}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Note: <InlineCode>timestamp</InlineCode> uses the format{' '}
          <InlineCode>yyyy-MM-dd HH:mm:ss</InlineCode> (not ISO-8601). Validation problems arrive
          in <InlineCode>fieldErrors[]</InlineCode> with machine-readable keys.
        </Typography>
      </Section>

      <Section title="Best Practices">
        <Box component="ul" sx={{ pl: 3, '& li': { mb: 1.5 } }}>
          <li>
            <strong>Store securely:</strong> Use environment variables or a secrets manager
            to store your API key. Never hardcode it in your source code.
          </li>
          <li>
            <strong>Server-side only:</strong> Make API calls from your backend server, not
            from client-side JavaScript or mobile apps.
          </li>
          <li>
            <strong>Rotate periodically:</strong> Regenerate your API key periodically and
            immediately if you suspect it has been compromised.
          </li>
          <li>
            <strong>Use sandbox for testing:</strong> Use the sandbox environment and
            sandbox API key for development and testing.
          </li>
        </Box>
      </Section>
    </Box>
  )
}
